/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 05 2024
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useMemo, useRef, useState } from "react";

import {
	Button,
	Column,
	Divider,
	Loader,
	TextField,
	Tooltip,
	Typography,
	useModal,
	useToast
} from "@/frameworks/designStuff/components";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";
import { Maybe } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { useByteCheck } from "@/frameworks/libraries/hooks";
import { RestAPIError } from "@/frameworks/libraries/restservice";

import { useChat, usePeople, useUsers } from "@/frameworks/core/API";
import { useChatRoomActions, useDMRoomState } from "@/frameworks/core/chat";
import { APIErrorCode, ModalID } from "@/frameworks/core/commons";
import { UserChip } from "@/frameworks/core/components";
import { useFriendUser } from "@/frameworks/core/hooks";
import { getFilteredUserListByValue } from "@/frameworks/core/utils";

import { IncludeConversation } from "../../IncludeConversation";
import * as S from "../emotion";
import UserToInvite from "./UserToInvite";
import { timer } from "rxjs";

interface InviteChatMemberProps extends React.HTMLAttributes<HTMLDivElement> {
	defaultUsersToInvite?: ValidateUserEmail[];
	channelID?: string;
	chatKind: ChatServices.RoomType;
	onHide: () => void;
}

export type ValidateUserEmail = {
	valid: boolean;
	name: string;
	userID: string;
	email: string;
	profileImagePath?: string;
};

export default function InviteDMChatMember(props: InviteChatMemberProps) {
	const [inviteList, setInviteList] = useState<ValidateUserEmail[]>(props.defaultUsersToInvite ?? []); // submit하는 최종 초대 리스트
	const [peopleList, setPeopleList] = useState<WebServices.People.List.Data[number]["users"]>([]); // 전체 피플 리스트 (중복 제거)
	const [searchEmailValue, setSearchEmailValue] = useState(""); // 검색어
	const [loading, setLoading] = useState(false);
	const searchResult =
		searchEmailValue === "" ? peopleList : getFilteredUserListByValue(peopleList, searchEmailValue);
	const peopleAPI = usePeople();
	const toast = useToast();
	const chatAPI = useChat();
	const modal = useModal();
	const { friends } = useFriendUser();
	const chatRoomActions = useChatRoomActions();

	const channelInfo = useDMRoomState(props.channelID ?? "");

	const chatUserList = useMemo(() => {
		if (!channelInfo) return [];
		const _channel = channelInfo as WebServices.DirectMessage.List.Data[number];
		return _channel.users.map((user) => user.id);
	}, [channelInfo]);

	const inviteUserEmailsRef = useRef<HTMLInputElement>(null);

	const createChannel = async (userIDs: string[]) => {
		try {
			const { id: channelID } = await chatRoomActions.dm.create({ userIDs });
			chatRoomActions.common.open({ channelID, type: "DM" });
			props.onHide();
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					// const DMRoom = chatRoomListActions.getDMRoom(roomId);
					// if (DMRoom?.hide) {
					// 	chatRoomActions.dm.hide({ channelID: roomId, hide: false });
					// }
					props.onHide();
					if (props.chatKind === "DM") {
						chatRoomActions.common.open({ channelID: roomId, type: "DM" });
					}
					return;
				}
			}
		}
	};
	const onSubmit = async (searchListOnlyUser?: WebServices.People.List.Data[number]["users"]) => {
		const inviteUserIDList = inviteList.map((e) => e.userID);

		const userIDList = searchListOnlyUser ? [searchListOnlyUser[0].userId, ...inviteUserIDList] : inviteUserIDList;
		const inviteUserList = peopleList.filter((obj) => userIDList.includes(obj.userId));

		const { data } = await chatAPI.dm.checkExist.execute({
			userIds: [
				...new Set([...(chatUserList || []), ...inviteUserList.map((user) => user.userId), Configure.userID])
			]
		});
		if (data?.exists) {
			// const DMRoom = chatRoomListActions.getDMRoom(data.roomId ?? "");
			// if (DMRoom?.hide) {
			// 	chatRoomActions.dm.hide({ channelID: data.roomId || "", hide: false });
			// }
			props.onHide();
			if (props.chatKind === "DM") {
				chatRoomActions.common.open({ channelID: data.roomId || "", type: "DM" });
			}
			return;
		}

		if (props.channelID) {
			modal.change(ModalID.INVITE_CHAT_MEMBER, {
				id: ModalID.INCLUDE_CONVERSATION,
				content: (
					<IncludeConversation
						inviteUserList={inviteUserList}
						onCancel={() => {
							modal.hide(ModalID.INCLUDE_CONVERSATION);
						}}
						onSubmit={(option) => {
							if (option === "createNewChat") {
								createChannel([...chatUserList, ...inviteUserList.map((user) => user.userId)]);
							} else {
								if (props.chatKind === "DM") {
									chatRoomActions.dm
										.addUsers({ users: inviteUserList, channelID: props.channelID! })
										.then(() => {
											modal.hideAll();
										});
								}
							}
							modal.hideAll();
						}}
					/>
				)
			});
			return;
		}

		createChannel(inviteUserList.map((user) => user.userId));
	};

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		const type = e.currentTarget.dataset["type"];

		if (type === "cancel") {
			props.onHide();
			return;
		} else {
			if (searchResult.length === 1) {
				onSubmit(searchResult);
			} else if (searchResult.length > 1 && inviteList.length < 1) {
				//이미 초대한 인원이 없고 + 검색어를 입력했을 경우
				//TODO 명확한 기획이 나오지 않아 기획이 나오면 문구 및 동작 수정 예정
				toast.show({ type: "error", message: _T("사용자를 추가해 주세요.") });
			} else {
				onSubmit();
				props.onHide();
			}
		}
	};

	const handleChangeField = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setSearchEmailValue(value);
	};

	const handleKeyDown = async (evt: React.KeyboardEvent<HTMLElement>) => {
		const target = evt.target as HTMLInputElement;

		//  검색목록에 유효한 이메일이 있다면, 첫번째 항목을 추가합니다.
		if (evt.key === "Enter" && !evt.shiftKey && evt.nativeEvent.isComposing === false) {
			// 1글자 이상 입력되어야 검색이 되도록 합니다.
			if (target.value.length < 1) return;
			//searchResult의 0번쨰를 inviteList에 넣어주면 됨 중복 판별해서
			const addUser = searchResult[0];
			if (addUser) {
				const result = await checkAuth(addUser.userId);
				if (!result) return;
				if (inviteList.map((e) => e.userID).includes(addUser.userId)) {
					// 이미 초대된 사람일 때
					setInviteList(inviteList.filter((e) => e.name !== addUser.name));
				} else {
					setInviteList((prev) => [
						...prev,
						{
							name: addUser.name,
							email: addUser.username,
							userID: addUser.userId,
							valid: true
						}
					]);
				}
			} else {
				setInviteList((prev) => [...prev, { name: target.value, email: "", userID: "", valid: false }]);
			}
			timer(0).subscribe(() => {
				target.value = "";
				setSearchEmailValue("");
			});
		}

		if (evt.key === "Backspace") {
			if (target.value.length > 0) return;
			const newArr = [...inviteList];
			newArr.pop();
			setInviteList(newArr);
		}
	};

	const checkAuth = async (userId: string) => {
		try {
			await chatAPI.dm.checkAuth.execute({ userId: userId });
			setLoading(false);
			return true;
		} catch (err) {
			console.debug("#inviteDM-err#", err);
			const error = err as RestAPIError;
			if (error.code === APIErrorCode.NOT_ALLOWED_DM_INVITE) {
				return false;
			}
		}
	};

	const handleDeleteUser = async (userID: string) => {
		const newList = inviteList.filter((invitedUser) => invitedUser.userID !== userID);

		if (newList.length === 1) {
			const lastUser = newList[0];
			const isValid = await checkAuth(lastUser.userID);

			setInviteList([
				{
					...lastUser,
					valid: isValid ?? true
				}
			]);
		} else {
			setInviteList(newList);
		}
	};

	const handleUserClick = async (user: WebServices.People.List.Data[number]["users"][number]) => {
		const userIsInList = inviteList.some((invitedUser) => invitedUser.userID === user.userId);

		if (userIsInList) {
			// 이미 초대된 사람인 경우
			await handleDeleteUser(user.userId);
		} else {
			const isValid = inviteList.length !== 0 || Boolean(channelInfo) ? true : await checkAuth(user.userId);

			setInviteList((prevList) => {
				const updatedPrevList =
					prevList.length > 0 ? prevList.map((item) => ({ ...item, valid: true })) : prevList;

				return [
					...updatedPrevList,
					{
						name: user.name,
						email: user.username,
						userID: user.userId,
						valid: isValid ?? true
					}
				];
			});
		}

		if (inviteUserEmailsRef.current) {
			inviteUserEmailsRef.current.value = "";
		}
	};

	const userAPI = useUsers();

	useEffect(() => {
		fetchDataUser();
		inviteUserEmailsRef.current?.focus();
	}, []);

	const fetchDataUser = async () => {
		try {
			const accountSetting = (await userAPI.account.inquiry.execute())?.data;
			const relatedUsers = (await peopleAPI.list.execute())?.data ?? [];
			const everyPeopleList = relatedUsers.flatMap((e) => e.users);

			if (
				accountSetting?.allowDmByFriend &&
				!accountSetting.allowDmByGroupUser &&
				!accountSetting.allowDmBySpaceUser &&
				friends
			) {
				const listPeople = everyPeopleList.filter((user) => {
					if (friends.some((friend) => user.userId === friend.userId)) {
						return user;
					}
				});
				setPeopleList(listPeople);
			} else if (
				accountSetting?.allowDmByFriend &&
				accountSetting.allowDmByGroupUser &&
				accountSetting.allowDmBySpaceUser
			) {
				const uniqueObjects = everyPeopleList.reduce(
					(list: WebServices.People.List.Data[number]["users"], compare) => {
						if (
							!list.some((obj) => obj.userId === compare.userId) &&
							!compare.blockedByMe &&
							!compare.blockedMe
						) {
							list.push(compare);
						}
						return list;
					},
					[]
				);
				setPeopleList(uniqueObjects);
			} else {
				const uniqueObjects = everyPeopleList.reduce(
					(list: WebServices.People.List.Data[number]["users"], compare) => {
						if (
							!list.some((obj) => obj.userId === compare.userId) &&
							!compare.blockedByMe &&
							!compare.blockedMe
						) {
							list.push(compare);
						}
						return list;
					},
					[]
				);
				setPeopleList(uniqueObjects);
			}
		} catch (error) {
			console.warn("#inviteDM-err#", error);
		}
	};

	const channelName =
		channelInfo?.users
			.map((user) => user.name)
			.slice(0, 3)
			.join(", ") ?? "";

	const { isOverLimit } = useByteCheck(50, channelName);

	useEffect(() => {
		if (channelInfo && channelInfo.deleted && channelInfo.roomJoined) {
			modal.hide(ModalID.INVITE_CHAT_MEMBER);
		}
	}, [channelInfo]);

	return (
		<S.Wrapper horizonAlign="left">
			<S.Header horizonAlign="distribute" verticalAlign="top">
				<Column>
					<Maybe
						test={Boolean(!channelInfo)}
						fallback={
							<Typography typoSize="Head6">
								{_T("{{channelName}}에 채팅 멤버 추가", {
									channelName: isOverLimit ? channelName.slice(0, 25).concat("...") : channelName
								})}
							</Typography>
						}
					>
						<Typography typoSize="Head6">{_T("새로운 채팅방을 개설합니다.")}</Typography>
					</Maybe>
					<Typography typoSize="Body4">
						{_T("채팅멤버 {{count}}명", {
							count: chatUserList.length
						})}
					</Typography>
				</Column>
				<S.HeaderIconList>
					<Button.Icon name="Close24" variant="text" size="large" onClick={() => props.onHide()} />
				</S.HeaderIconList>
			</S.Header>
			<S.Body horizonAlign="center">
				<Divider type="line" />
				<S.InnerBody>
					<S.InnerContent>
						<TextField.Tag
							help={_T("이름과 이메일로 검색해주세요.")}
							onChange={handleChangeField}
							onKeyDown={handleKeyDown}
							minHeight="40px"
							ref={inviteUserEmailsRef}
							tags={((): any => {
								return inviteList.map((chipData) => (
									<Maybe test={chipData.valid} key={`${chipData.name}`}>
										<UserChip
											userID={chipData.userID || chipData.name}
											isValid={true}
											key={chipData.userID}
											handleClose={() => {
												handleDeleteUser(chipData.userID);
											}}
											isEllipsisText
										/>
										<Maybe.Fallback>
											<Tooltip
												message={
													chipData.email.length === 0
														? _T("다시 확인해주세요.")
														: _T("DM 권한이 없는 사용자 입니다.")
												}
												hasIcon={true}
											>
												<UserChip
													userID={chipData.userID || chipData.name}
													isValid={false}
													isError={chipData.email.length !== 0}
													key={chipData.name}
													handleClose={() => {
														handleDeleteUser(chipData.userID);
													}}
													icon={{ name: "Info24" }}
													isEllipsisText
												/>
											</Tooltip>
										</Maybe.Fallback>
									</Maybe>
								));
							})()}
						/>
					</S.InnerContent>
					<S.UserList css={[hoverScrollCss()]}>
						<Maybe test={!loading} fallback={<Loader />}>
							{searchResult.map((user) => (
								<UserToInvite
									key={user.userId}
									userID={user.userId}
									onSelectUser={() => handleUserClick(user)}
									disabled={chatUserList?.includes(user.userId)}
									selected={inviteList.map((inviteUser) => inviteUser.email).includes(user.username)}
									me={user.me}
								/>
							))}
						</Maybe>
					</S.UserList>
				</S.InnerBody>
			</S.Body>
			<S.Footer horizonAlign="center" verticalAlign="center">
				<Button.Group>
					<Button
						filled="secondary"
						onClick={handleClick}
						data-type="cancel"
						css={{ minWidth: 120 }}
						color="neutral_80_100"
					>
						{_T("취소")}
					</Button>
					<Button
						onClick={handleClick}
						data-type="confirm"
						disabled={
							(inviteList.length <= 0 && searchEmailValue.length <= 0) ||
							inviteList.filter((chip) => !chip.valid).length > 0 ||
							searchResult.length === 0
						}
						css={{ minWidth: 120 }}
					>
						{_T("적용하기")}
					</Button>
				</Button.Group>
			</S.Footer>
		</S.Wrapper>
	);
}
